/**
 * useControleAcesso Hook - Access control data and handlers
 */
import { useState, useEffect, useCallback } from 'react';
import permissoesService, { MatrizPermissoes, PermissaoEfetiva } from '../services/permissoesService';
import perfisService, { Perfil } from '../services/perfisService';
import userService, { User } from '../services/userService';
import { msg } from '../utils/messages';

type TipoPermissao = 'pode_visualizar' | 'pode_criar' | 'pode_editar' | 'pode_excluir' | 'pode_exportar';

export function useControleAcesso() {
  const [activeTab, setActiveTab] = useState<'matriz' | 'usuarios'>('matriz');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matriz, setMatriz] = useState<MatrizPermissoes | null>(null);
  const [perfis, setPerfis] = useState<Perfil[]>([]);

  // User permissions states
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [selectedUsuario, setSelectedUsuario] = useState<User | null>(null);
  const [permissoesUsuario, setPermissoesUsuario] = useState<PermissaoEfetiva[]>([]);
  const [loadingUsuario, setLoadingUsuario] = useState(false);

  // Load main data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [matrizData, perfisData] = await Promise.all([
        permissoesService.buscarMatrizPermissoes(),
        perfisService.listarPerfis()
      ]);

      setMatriz(matrizData);
      setPerfis(perfisData);
    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
      setError(err?.response?.data?.detail || 'Erro ao carregar dados de permissões');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle permission change in matrix
  const handlePermissaoChange = useCallback(async (
    perfilId: number,
    telaId: number,
    tipoPermissao: TipoPermissao,
    valor: boolean
  ) => {
    if (!matriz) return;

    try {
      const permissaoAtual = matriz.matriz.find(
        p => p.perfil_id === perfilId && p.tela_id === telaId
      );

      if (!permissaoAtual) return;

      const permissoesAtualizadas = {
        pode_visualizar: permissaoAtual.pode_visualizar,
        pode_criar: permissaoAtual.pode_criar,
        pode_editar: permissaoAtual.pode_editar,
        pode_excluir: permissaoAtual.pode_excluir,
        pode_exportar: permissaoAtual.pode_exportar,
        [tipoPermissao]: valor
      };

      // Business rule: If unchecking "View", also uncheck create/edit/delete
      if (tipoPermissao === 'pode_visualizar' && !valor) {
        permissoesAtualizadas.pode_criar = false;
        permissoesAtualizadas.pode_editar = false;
        permissoesAtualizadas.pode_excluir = false;
      }

      // Business rule: If checking create/edit/delete, must also check view
      if ((tipoPermissao === 'pode_criar' || tipoPermissao === 'pode_editar' || tipoPermissao === 'pode_excluir') && valor) {
        permissoesAtualizadas.pode_visualizar = true;
      }

      await permissoesService.atualizarPermissaoPerfil(perfilId, telaId, permissoesAtualizadas);

      setMatriz(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          matriz: prev.matriz.map(p =>
            p.perfil_id === perfilId && p.tela_id === telaId
              ? { ...p, ...permissoesAtualizadas }
              : p
          )
        };
      });
    } catch (err: any) {
      console.error('Erro ao atualizar permissão:', err);
      msg.error('Erro ao atualizar permissão. Tente novamente.');
    }
  }, [matriz]);

  // Load users list
  const loadUsuarios = useCallback(async () => {
    try {
      setLoadingUsuario(true);
      const usersData = await userService.getUsers();
      setUsuarios(usersData.filter(u => u.ativo));
    } catch (err: any) {
      console.error('Erro ao carregar usuários:', err);
      msg.error('Erro ao carregar lista de usuários');
    } finally {
      setLoadingUsuario(false);
    }
  }, []);

  // Load user permissions
  const loadPermissoesUsuario = useCallback(async (userId: number) => {
    try {
      setLoadingUsuario(true);
      const permissoes = await permissoesService.buscarPermissoesUsuario(userId);
      setPermissoesUsuario(permissoes);
    } catch (err: any) {
      console.error('Erro ao carregar permissões do usuário:', err);
      msg.error('Erro ao carregar permissões do usuário');
    } finally {
      setLoadingUsuario(false);
    }
  }, []);

  // Handle user permission change
  const handleUsuarioPermissaoChange = useCallback(async (
    telaId: number,
    tipoPermissao: TipoPermissao,
    valor: boolean
  ) => {
    if (!selectedUsuario) return;

    try {
      const permissaoAtual = permissoesUsuario.find(p => p.tela_id === telaId);
      if (!permissaoAtual) return;

      const permissaoAtualizada = {
        usuario_id: selectedUsuario.id,
        tela_id: telaId,
        pode_visualizar: permissaoAtual.pode_visualizar,
        pode_criar: permissaoAtual.pode_criar,
        pode_editar: permissaoAtual.pode_editar,
        pode_excluir: permissaoAtual.pode_excluir,
        pode_exportar: permissaoAtual.pode_exportar,
        [tipoPermissao]: valor
      };

      await permissoesService.criarPermissaoUsuario(permissaoAtualizada);
      await loadPermissoesUsuario(selectedUsuario.id);
    } catch (err: any) {
      console.error('Erro ao atualizar permissão:', err);
      msg.error('Erro ao atualizar permissão do usuário. Tente novamente.');
    }
  }, [selectedUsuario, permissoesUsuario, loadPermissoesUsuario]);

  // Select user
  const handleSelectUsuario = useCallback((user: User) => {
    setSelectedUsuario(user);
    loadPermissoesUsuario(user.id);
  }, [loadPermissoesUsuario]);

  // Switch to usuarios tab
  const handleTabChange = useCallback((tab: 'matriz' | 'usuarios') => {
    setActiveTab(tab);
    if (tab === 'usuarios' && usuarios.length === 0) {
      loadUsuarios();
    }
  }, [usuarios.length, loadUsuarios]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Tab state
    activeTab,
    handleTabChange,

    // Loading states
    isLoading,
    error,
    loadingUsuario,

    // Matrix data
    matriz,
    perfis,
    handlePermissaoChange,

    // User data
    usuarios,
    selectedUsuario,
    permissoesUsuario,
    handleSelectUsuario,
    handleUsuarioPermissaoChange,

    // Actions
    loadData,
  };
}
